import { useTranslation } from "react-i18next";
import * as sup from "superstruct";

import { emailPattern, notEmpty } from "@/modules/form/utils/structs";
import { Button } from "@/modules/theme/components/Button/Button";
import { DialogButtonsWrapper } from "@/modules/theme/components/Dialog/components/DialogButtonsWrapper";
import { DialogContentWrapper } from "@/modules/theme/components/Dialog/components/DialogContentWrapper";
import { Dialog } from "@/modules/theme/components/Dialog/Dialog";
import { recompose } from "@/utils/structs/recompose";

import { Form } from "../../../modules/form/components/Form/Form";
import { TextInput } from "../../../modules/form/components/TextInput/TextInput";
import { useForgotPasswordMutation } from "../hooks/useForgotPasswordMutation";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const forgotPasswordStruct = sup.object({
  email: recompose(sup.string(), notEmpty, emailPattern),
});

const translationNamespace = "pages/SignInPage";

export function ResetPasswordDialog({ onClose, isOpen }: DialogProps) {
  const { t } = useTranslation(translationNamespace);
  const forgotPasswordMutation = useForgotPasswordMutation();

  return (
    <Dialog
      isOpen={isOpen || forgotPasswordMutation.status === "loading"}
      onClose={onClose}
      title={t("Reset Password")}
      description={t(
        "Enter your email address with which you registered the account. We will send you a link to change your password."
      )}
    >
      <Form
        struct={forgotPasswordStruct}
        defaultValues={{
          email: "",
        }}
        onValidSubmit={forgotPasswordMutation.mutate}
      >
        {({ register }) => (
          <>
            <DialogContentWrapper>
              <TextInput
                label={t("Email")}
                placeholder={t("example@example.com")}
                {...register("email")}
                errorMessages={{
                  "string.notEmpty": t("Email is required"),
                  "string.emailPattern": t("Invalid email address"),
                }}
              />
            </DialogContentWrapper>
            <DialogButtonsWrapper>
              <Button
                isDisabled={forgotPasswordMutation.status === "loading"}
                variant="secondary"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                isLoading={forgotPasswordMutation.status === "loading"}
                isDisabled={forgotPasswordMutation.status === "loading"}
              >
                {t("Send")}
              </Button>
            </DialogButtonsWrapper>
          </>
        )}
      </Form>
    </Dialog>
  );
}
